
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useCallAnalytics } from './useCallAnalytics';
import { useTwilioContext } from '@/context/TwilioContext';

export const useCallAwareAnalytics = () => {
  const { callInProgress } = useTwilioContext();
  const wasInCall = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const frozenDataRef = useRef<any>(null);
  
  // Use analytics hook with shouldFetch based on call state
  const analyticsResult = useCallAnalytics({ 
    shouldFetch: !callInProgress 
  });

  // Freeze data during calls to prevent UI flickering
  const stableData = useMemo(() => {
    if (callInProgress && frozenDataRef.current) {
      // Return frozen data during calls
      return frozenDataRef.current;
    }
    
    if (!callInProgress && (analyticsResult.recentCalls || analyticsResult.todayAnalytics)) {
      // Update frozen data when not in call
      frozenDataRef.current = {
        todayAnalytics: analyticsResult.todayAnalytics,
        recentCalls: analyticsResult.recentCalls,
        loading: false,
        error: analyticsResult.error
      };
      return frozenDataRef.current;
    }
    
    // First load or no data yet
    return {
      todayAnalytics: analyticsResult.todayAnalytics,
      recentCalls: analyticsResult.recentCalls,
      loading: analyticsResult.loading,
      error: analyticsResult.error
    };
  }, [callInProgress, analyticsResult.todayAnalytics, analyticsResult.recentCalls, analyticsResult.loading, analyticsResult.error]);

  // Stable refresh function to avoid re-renders
  const stableRefresh = useCallback(() => {
    // Clear any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Delay the refresh to avoid immediate re-render loops
    refreshTimeoutRef.current = setTimeout(() => {
      console.log('useCallAwareAnalytics: Call ended, refreshing analytics (delayed)');
      analyticsResult.refetch();
    }, 2000); // 2 second delay
  }, [analyticsResult.refetch]);

  // Refresh analytics when call ends
  useEffect(() => {
    // Only trigger refresh when transitioning from in-call to not-in-call
    if (wasInCall.current && !callInProgress) {
      stableRefresh();
    }
    
    // Update the ref to track call state
    wasInCall.current = callInProgress;
    
    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [callInProgress, stableRefresh]);

  return {
    ...stableData,
    refetch: analyticsResult.refetch
  };
};
